import type {GetAiFeatureUsage_aiFeatureUsage_features as AiFeature} from 'nutshell-graphql-types';

import type {FeatureGroup, FeatureGroupRow} from './feature-config';

export function getProgressBarPercent(limit?: number | null, remaining?: number | null) {
    return typeof limit === 'number' && typeof remaining === 'number'
        ? (limit - remaining) / limit
        : undefined;
}

export function getUsageText(
    limit: number | null | undefined,
    remaining: number | null | undefined,
    wordForUsage: string
) {
    return !limit
        ? `You have unlimited ${wordForUsage} available.`
        : `You have ${
              typeof remaining === 'number' ? remaining : '…'
          } ${wordForUsage} left this month, out of a total of ${limit}.`;
}

/**
 * Groups individual feature settings into display rows by summing usage
 * and merging enabled state (all must be enabled for the group to show as enabled).
 */
export function groupFeatures(features: AiFeature[], groups: FeatureGroup[]): FeatureGroupRow[] {
    const featureMap = new Map(features.map((feature) => [feature.type, feature]));

    return groups
        .map((group): FeatureGroupRow | null => {
            const matchedFeatures = group.types
                .map((type) => featureMap.get(type))
                .filter((feature): feature is AiFeature => feature !== undefined);

            if (matchedFeatures.length === 0) {
                return null;
            }

            return {
                displayName: group.displayName,
                description: group.description,
                isEnabled: matchedFeatures.every((feature) => feature.isEnabled),
                requiresUpgrade: matchedFeatures.some((feature) => feature.requiresUpgrade),
                totalUsage: matchedFeatures.reduce((sum, feature) => sum + feature.usage, 0),
                settingName: group.settingName,
                types: group.types,
                canDisableGlobally: matchedFeatures.every((feature) => feature.canDisableGlobally),
            };
        })
        .filter((row): row is FeatureGroupRow => row !== null);
}
